import { useEffect, useState } from 'react';
import Footer from '../home/components/Footer';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface PageStats {
  page_title: string;
  page_path: string;
  total_views: number;
  unique_sessions: number;
  avg_scroll_depth: number;
  avg_time_on_page: number;
  total_downloads: number;
}

export default function AnalyticsDashboard() {
  const [stats, setStats] = useState<PageStats[], []>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | 'all'>('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Calculate date filter
      let dateFilter = '';
      if (timeRange === '7d') {
        dateFilter = `created_at.gte.${new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()}`;
      } else if (timeRange === '30d') {
        dateFilter = `created_at.gte.${new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()}`;
      }

      // Fetch all analytics data
      const query = supabase
        .from('whitepaper_analytics')
        .select('*');

      if (dateFilter) {
        const date = new Date(Date.now() - (timeRange === '7d' ? 7 : 30) * 24 * 60 * 60 * 1000);
        query.gte('created_at', date.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      // Process data to calculate stats
      const pageMap = new Map<string, {
        views: number;
        sessions: Set<string>;
        scrollDepths: number[];
        timeOnPage: number[];
        downloads: number;
        title: string;
      }>();

      data?.forEach(event => {
        if (!pageMap.has(event.page_path)) {
          pageMap.set(event.page_path, {
            views: 0,
            sessions: new Set(),
            scrollDepths: [],
            timeOnPage: [],
            downloads: 0,
            title: event.page_title.split(' - ')[0] // Get base title
          });
        }

        const pageData = pageMap.get(event.page_path)!;

        if (event.event_type === 'page_view') {
          pageData.views++;
          pageData.sessions.add(event.session_id);
        } else if (event.event_type === 'scroll_depth') {
          const depth = parseInt(event.page_title.match(/(\d+)%/)?.[1] || '0');
          pageData.scrollDepths.push(depth);
        } else if (event.event_type === 'time_on_page') {
          const time = parseInt(event.page_title.match(/(\d+)s/)?.[1] || '0');
          pageData.timeOnPage.push(time);
        } else if (event.event_type === 'pdf_download') {
          pageData.downloads++;
        }
      });

      // Convert to stats array
      const statsArray: PageStats[] = Array.from(pageMap.entries()).map(([path, data]) => ({
        page_path: path,
        page_title: data.title,
        total_views: data.views,
        unique_sessions: data.sessions.size,
        avg_scroll_depth: data.scrollDepths.length > 0
          ? Math.round(data.scrollDepths.reduce((a, b) => a + b, 0) / data.scrollDepths.length)
          : 0,
        avg_time_on_page: data.timeOnPage.length > 0
          ? Math.round(data.timeOnPage.reduce((a, b) => a + b, 0) / data.timeOnPage.length)
          : 0,
        total_downloads: data.downloads
      }));

      // Sort by total views
      statsArray.sort((a, b) => b.total_views - a.total_views);

      setStats(statsArray);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-6 lg:px-12 py-20 max-w-7xl">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            White Paper Analytics
          </h1>
          <p className="text-xl text-slate-600">
            Track engagement and readership across your research publications
          </p>
        </div>

        {/* Time Range Filter */}
        <div className="mb-8 flex gap-4">
          <button
            onClick={() => setTimeRange('7d')}
            className={`px-6 py-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              timeRange === '7d'
                ? 'bg-red-800 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Last 7 Days
          </button>
          <button
            onClick={() => setTimeRange('30d')}
            className={`px-6 py-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              timeRange === '30d'
                ? 'bg-red-800 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Last 30 Days
          </button>
          <button
            onClick={() => setTimeRange('all')}
            className={`px-6 py-3 font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              timeRange === 'all'
                ? 'bg-red-800 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            All Time
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-red-800 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-slate-600">Loading analytics...</p>
          </div>
        )}

        {/* Stats Grid */}
        {!loading && stats.length === 0 && (
          <div className="text-center py-12 bg-slate-50 rounded-lg">
            <i className="ri-bar-chart-line text-6xl text-slate-300 mb-4"></i>
            <p className="text-xl text-slate-600">No analytics data available yet</p>
            <p className="text-slate-500 mt-2">Data will appear as visitors engage with your white papers</p>
          </div>
        )}

        {!loading && stats.length > 0 && (
          <div className="space-y-6">
            {stats.map((stat, index) => (
              <div key={stat.page_path} className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">
                      #{index + 1} {stat.page_title}
                    </h3>
                    <p className="text-sm text-slate-500">{stat.page_path}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-eye-line text-xl text-red-800"></i>
                      </div>
                      <span className="text-sm text-slate-600">Total Views</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{stat.total_views}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-user-line text-xl text-red-800"></i>
                      </div>
                      <span className="text-sm text-slate-600">Unique Visitors</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{stat.unique_sessions}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-download-line text-xl text-red-800"></i>
                      </div>
                      <span className="text-sm text-slate-600">Downloads</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{stat.total_downloads}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-arrow-down-line text-xl text-red-800"></i>
                      </div>
                      <span className="text-sm text-slate-600">Avg Scroll Depth</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">
                      {stat.avg_scroll_depth > 0 ? `${stat.avg_scroll_depth}%` : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-time-line text-xl text-red-800"></i>
                      </div>
                      <span className="text-sm text-slate-600">Avg Time on Page</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">
                      {stat.avg_time_on_page > 0 ? formatTime(stat.avg_time_on_page) : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Engagement Score */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Engagement Score</span>
                    <div className="flex items-center gap-2">
                      {stat.avg_scroll_depth >= 75 && stat.avg_time_on_page >= 60 && (
                        <>
                          <span className="text-sm font-semibold text-green-700">High</span>
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </>
                      )}
                      {stat.avg_scroll_depth >= 50 && stat.avg_scroll_depth < 75 && (
                        <>
                          <span className="text-sm font-semibold text-yellow-700">Medium</span>
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        </>
                      )}
                      {stat.avg_scroll_depth < 50 && (
                        <>
                          <span className="text-sm font-semibold text-slate-500">Low</span>
                          <div className="w-3 h-3 bg-slate-400 rounded-full"></div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-12 bg-slate-50 border-l-4 border-red-800 p-6 rounded-lg">
          <h3 className="text-lg font-bold text-slate-900 mb-2">About These Metrics</h3>
          <ul className="space-y-2 text-slate-700">
            <li><strong>Total Views:</strong> Number of times the page was loaded</li>
            <li><strong>Unique Visitors:</strong> Number of distinct browsing sessions</li>
            <li><strong>Downloads:</strong> Number of PDF downloads tracked</li>
            <li><strong>Avg Scroll Depth:</strong> How far down the page visitors scroll on average</li>
            <li><strong>Avg Time on Page:</strong> Average duration visitors spend reading</li>
            <li><strong>Engagement Score:</strong> High = 75%+ scroll depth and 60+ seconds; Medium = 50-75% scroll depth</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
}
